import {
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../lib/firebase";
import {
  Collections,
  type EventLocationCreate,
  type BulkCreateEventLocationsInput,
  type BulkCreateEventLocationsResponse,
  type BulkResolveEventLocationsInput,
  type BulkResolveEventLocationsResponse,
} from "@event-planner/shared";
import type { LocationPickerValue } from "../components/forms/LocationPicker";

/**
 * Bulk-create eventLocations from the organizer-workbook upload. Inputs
 * are pre-validated by `locationImportSpec` in the dialog; the callable
 * handles authorization + duplicate-label detection + atomic writes.
 */
const bulkCreateEventLocationsCallable = httpsCallable<
  BulkCreateEventLocationsInput,
  BulkCreateEventLocationsResponse
>(functions, "bulkCreateEventLocations");

export async function bulkCreateEventLocations(
  input: BulkCreateEventLocationsInput,
): Promise<BulkCreateEventLocationsResponse> {
  const result = await bulkCreateEventLocationsCallable(input);
  return result.data;
}

/**
 * Phase B preview path: returns per-row Apple Maps proposals without
 * writing. The dialog's reviewing phase populates from this; on confirm
 * the dialog passes accepted rows back to `bulkCreateEventLocations`.
 */
const bulkResolveEventLocationsCallable = httpsCallable<
  BulkResolveEventLocationsInput,
  BulkResolveEventLocationsResponse
>(functions, "bulkResolveEventLocations");

export async function bulkResolveEventLocations(
  input: BulkResolveEventLocationsInput,
): Promise<BulkResolveEventLocationsResponse> {
  const result = await bulkResolveEventLocationsCallable(input);
  return result.data;
}

/**
 * Direct Firestore writes for the eventLocations collection. No callable
 * wrapper needed — this collection has no cross-document fan-out unlike
 * eventGroups (the onEventLocationUpdated trigger handles label fanout
 * automatically when a location's label changes).
 */

export type CreateEventLocationInput = EventLocationCreate;

export type UpdateEventLocationInput = Partial<
  Omit<CreateEventLocationInput, "eventId">
>;

export async function createEventLocation(
  input: CreateEventLocationInput,
): Promise<string> {
  const docRef = await addDoc(collection(db, Collections.EVENT_LOCATIONS), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateEventLocation(
  locationId: string,
  input: UpdateEventLocationInput,
): Promise<void> {
  await updateDoc(doc(db, Collections.EVENT_LOCATIONS, locationId), {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteEventLocation(locationId: string): Promise<void> {
  await deleteDoc(doc(db, Collections.EVENT_LOCATIONS, locationId));
}

/**
 * Resolve a `LocationPickerValue` to a concrete locationId for persisting
 * onto a parent entity (Event.venueLocationId / Session.locationId /
 * EventGroup.locationId / TravelItem.pickupLocationId or dropoffLocationId).
 *
 * Behaviors per branch (driven by the picker's locationId + originalLocationId):
 *   - value === null                       → null (no pin set)
 *   - empty content                        → null (treat empty as no-pin)
 *   - locationId set                       → update that doc, return id
 *                                            ("Editing" mode in the picker UI)
 *   - locationId null + originalLocationId → create new, return new id
 *                                            ("New copy" — fork; original
 *                                             doc is intentionally untouched)
 *   - both null                            → create new, return id
 *                                            (Pure new authoring session)
 *
 * "Has content" = non-empty label OR coordinates OR any address part.
 *
 * The onEventLocationUpdated CF trigger handles label fanout to Sessions
 * and EventGroups that reference the updated location.
 */
export async function resolveLocationPickerValue(
  value: LocationPickerValue,
  eventId: string,
): Promise<string | null> {
  if (!value) return null;
  const hasContent =
    !!value.label.trim() ||
    !!value.coordinates ||
    !!value.address ||
    !!value.city ||
    !!value.stateOrRegion ||
    !!value.country;
  if (!hasContent) return null;
  const payload = {
    label: value.label,
    coordinates: value.coordinates,
    address: value.address,
    city: value.city,
    stateOrRegion: value.stateOrRegion,
    country: value.country,
    color: value.color,
    verified: value.verified,
  };
  if (value.locationId) {
    // Editing mode — update the existing doc in place. The picker's
    // toggle button surfaces this choice to the organizer; the optional
    // save-flow dialog provides one more confirmation step before this
    // mutation happens.
    await updateEventLocation(value.locationId, payload);
    return value.locationId;
  }
  // New-copy mode (or pure new). Either way: create a fresh doc. If
  // originalLocationId was set, the user explicitly chose to fork, and
  // the original doc stays intact.
  return await createEventLocation({ eventId, ...payload });
}
